import { useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Contacts, PermissionStatus } from '@capacitor-community/contacts';

export interface DeviceContact {
  id: string;
  name: string;
  phoneNumbers: string[];
  initials: string;
}

interface UseContactSyncReturn {
  contacts: DeviceContact[];
  loading: boolean;
  error: string | null;
  permissionStatus: PermissionStatus | null;
  isNative: boolean;
  requestPermissionAndFetch: () => Promise<void>;
}

export function useContactSync(): UseContactSyncReturn {
  const [contacts, setContacts] = useState<DeviceContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus | null>(null);

  const isNative = Capacitor.isNativePlatform();

  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const requestPermissionAndFetch = useCallback(async () => {
    if (!isNative) {
      setError('Contact sync is only available on mobile devices');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check current permission status
      console.log('[Contacts] Checking permissions...');
      const status = await Contacts.checkPermissions();
      console.log('[Contacts] Permission status:', JSON.stringify(status));
      setPermissionStatus(status);

      // If not granted or limited, request permission
      const hasAccess = status.contacts === 'granted' || status.contacts === 'limited';
      if (!hasAccess) {
        console.log('[Contacts] Requesting permissions...');
        const requestResult = await Contacts.requestPermissions();
        console.log('[Contacts] Request result:', JSON.stringify(requestResult));
        setPermissionStatus(requestResult);

        const hasAccessAfterRequest = requestResult.contacts === 'granted' || requestResult.contacts === 'limited';
        if (!hasAccessAfterRequest) {
          setError('Permission to access contacts was denied');
          setLoading(false);
          return;
        }
      }

      // Fetch contacts
      console.log('[Contacts] Fetching contacts...');
      const result = await Contacts.getContacts({
        projection: {
          name: true,
          phones: true,
        },
      });
      console.log('[Contacts] Got', result.contacts.length, 'raw contacts');

      // Transform and filter contacts
      const formattedContacts: DeviceContact[] = result.contacts
        .filter(contact => contact.name?.display && contact.phones?.length)
        .map(contact => ({
          id: contact.contactId || crypto.randomUUID(),
          name: contact.name?.display || 'Unknown',
          phoneNumbers: contact.phones?.map(p => p.number || '').filter(Boolean) || [],
          initials: getInitials(contact.name?.display || '?'),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      console.log('[Contacts] Formatted', formattedContacts.length, 'contacts (with name + phone)');
      setContacts(formattedContacts);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      console.error('[Contacts] Error:', errMsg, err);
      setError(`Failed to fetch contacts: ${errMsg}`);
    } finally {
      setLoading(false);
    }
  }, [isNative]);

  return {
    contacts,
    loading,
    error,
    permissionStatus,
    isNative,
    requestPermissionAndFetch,
  };
}
